/**
 * The "motion" timeline column type (W2-04a; ARCHITECTURE.md §7.7, DESIGN.md §4.5, §4.7): the column factory the
 * module contributes (`timelineColumns`) and the controller that keeps the host in step with the editor:
 *
 *  - `instances(host)` returns one instance per motion column (columnSpec.ts): the active line's columns and the
 *    pinned ones, each with its collapsed column view (render/timeline/motion/motionColumn.ts), the React header
 *    (ColumnHeader.tsx, portalled by the timeline panel) and a default width (pairs wider, sparklines narrow).
 *    A view lives as long as its instance id; the host disposes views whose instance left the layout.
 *  - The controller relayouts the host when the column SET changes (active line, active layer, pinned columns,
 *    "show motion columns", the expanded column, the level, a property or layer appearing or disappearing) and
 *    redraws motion columns when what they draw changes outside the document (selection, focused column, the
 *    segment placement and paste ghosts, layer mutes).
 *  - Each instance declares its region 'motion' (focus / hover atoms of the keyboard router, so the RPE preset's
 *    R…R works "over a motion column"), its context menu 'motion.context' and its accessible name ("Rotation, Main"
 *    / "Rotation, layer 1, Main"); the host puts them on the column element (TimelineColumnInstance). The name is
 *    also refreshed on every content draw (a layer switch renames a column without a relayout).
 * Small screens: when the timeline sits in the shell's sparkline mode (`data-motion-columns="sparklines"`, DESIGN.md
 * §3.3), columns are narrow sparklines until their header is clicked. Below 1024 px the shell says "drawer": no
 * package hosts a bottom drawer for the columns yet (CR-W2-04a-7), so they stay in the timeline as sparklines there
 * too, which keeps the lane wide. The controller watches that attribute, so the columns switch as soon as the
 * layout preset changes.
 */

import type { ComponentType } from 'react';
import type { AppServices } from '../../app/registry/types';
import type { Unsubscribe } from '../../core/util/types';
import type { TimelineColumnDef, TimelineColumnInstance, TimelineHostApi } from '../../render/types';
import { asTimelineHostController } from '../../render/timeline/TimelineHost';
import { createMotionColumnView, type MotionColumnView } from '../../render/timeline/motion/motionColumn';
import type { DocStore, EditorStore } from '../../state/types';
import { createColumnHeader } from './ColumnHeader';
import { columnOverlay, columnScene, dropSceneCache } from './columnScene';
import { columnsSignature, computeColumns, findLane, isLayered, type ColumnSpec } from './columnSpec';
import { propNames } from './format';
import { levelOf } from './motionActions';
import { createMotionHandlers } from './motionGestures';
import { m } from './motion.messages';
import { motionSession, type MotionSession } from './session';

export const MOTION_DEF_ID = 'motion';
/** Column type of the motion columns drawn as sparklines (narrow screens, or next to the expanded curve editor). */
export const MOTION_SPARK_DEF_ID = 'motion-spark';
/** Placement of motion columns: after the lanes (20), before effects (40); W2-04b's curve editor is 31. */
export const MOTION_ORDER = 30;
/**
 * A full motion column never gets narrower than 60 px (UX-034): the timeline scrolls sideways instead of shrinking
 * the columns into unreadable stubs. Sparkline columns have their own column type (40 px).
 */
export const MOTION_WIDTH = { min: 60, default: 96, max: 360 } as const;
export const MOTION_SPARK_WIDTH = { min: 40, default: 40, max: 120 } as const;
const PAIR_WIDTH = 156;
const COMPACT_WIDTH = MOTION_SPARK_WIDTH.default;

export interface MotionController {
  readonly session: MotionSession;
  readonly def: TimelineColumnDef;
  /** The sparkline columns (MOTION_SPARK_DEF_ID): the same columns as `def`, the compact ones. */
  readonly sparkDef: TimelineColumnDef;
  specOf(id: string): ColumnSpec | null;
  /** True when the column is drawn as a sparkline. */
  compact(id: string): boolean;
  /** Toggle a sparkline column to full width (small screens). */
  toggleCompactExpanded(id: string): void;
  /** Recompute the column set now; relayout when it changed. */
  refresh(): void;
  dispose(): void;
}

const controllers = new WeakMap<MotionSession, MotionController>();

/** The controller of an app's motion columns (created on first use; `init` and the column factory share it). */
export function motionController(services: AppServices): MotionController {
  const session = motionSession(services, true);
  let c = controllers.get(session);
  if (c === undefined) {
    c = createController(session);
    controllers.set(session, c);
  }
  return c;
}

const MODE_ATTR = 'data-motion-columns';

/** The shell's element that says how motion columns are shown (`data-motion-columns`), around `el`. */
function modeElementOf(el: Element | null): Element | null {
  if (el === null || typeof el.closest !== 'function') return null;
  return el.closest(`[${MODE_ATTR}]`);
}

function hostRoot(host: TimelineHostApi | null): HTMLElement | null {
  if (host === null) return null;
  return asTimelineHostController(host)?.root ?? null;
}

/** Modes in which the columns are drawn as sparklines ("drawer": see CR-W2-04a-7). */
const COMPACT_MODES: ReadonlySet<string> = new Set(['sparklines', 'drawer']);

function isSparklines(modeEl: Element | null): boolean {
  const mode = modeEl?.getAttribute(MODE_ATTR);
  return mode !== null && mode !== undefined && COMPACT_MODES.has(mode);
}

/** The host's element of a column instance (created synchronously by a relayout), or null. */
function columnElementIn(root: HTMLElement | null, id: string): HTMLElement | null {
  if (root === null) return null;
  for (const el of root.querySelectorAll<HTMLElement>('[data-tl-col]')) if (el.dataset.tlCol === id) return el;
  return null;
}

function createController(session: MotionSession): MotionController {
  const services = (): AppServices => session.services;
  const instances = new Map<string, TimelineColumnInstance>();
  const views = new Map<string, MotionColumnView>();
  const compactExpanded = new Set<string>();
  let sparkline = false;
  let lastSignature = '';
  const offs: Unsubscribe[] = [];
  let storeOffs: Unsubscribe[] = [];
  let disposed = false;
  /** The watched `[data-motion-columns]` element and its observer. */
  let modeEl: Element | null = null;
  let modeObserver: MutationObserver | null = null;

  /** Watches the shell's sparkline attribute around `el` (the host root or a column element). */
  const watchMode = (el: Element | null): void => {
    const next = modeElementOf(el);
    if (next === null || next === modeEl || disposed) return;
    modeObserver?.disconnect();
    modeObserver = null;
    modeEl = next;
    if (typeof MutationObserver === 'undefined') return;
    modeObserver = new MutationObserver(() => {
      if (isSparklines(modeEl) !== sparkline) session.relayout();
    });
    modeObserver.observe(next, { attributes: true, attributeFilter: [MODE_ATTR] });
  };

  const specOf = (id: string): ColumnSpec | null => session.specs.find((c) => c.id === id) ?? null;
  /**
   * Sparklines: the shell's narrow-screen mode, and every other column while the curve editor is expanded (UX-039:
   * the editor gets the room; a click on a sparkline shows that column in full again).
   */
  const compact = (id: string): boolean =>
    (sparkline || services().editor.getState().timeline.expandedColumn !== null) && !compactExpanded.has(id);

  const compute = (): ColumnSpec[] => {
    const s = services();
    const store = s.store;
    if (store === null) return [];
    return computeColumns({ doc: store.doc, idx: store.idx, editor: s.editor.getState(), level: levelOf(s) });
  };

  const handlers = createMotionHandlers({ session, specOf });

  let header: ComponentType<{ readonly instanceId: string }> | null = null;
  const headerComponent = (): ComponentType<{ readonly instanceId: string }> => {
    header ??= createColumnHeader(controller);
    return header;
  };

  /** Accessible name of a column: "Rotation, Main"; a layer other than the base one is named ("layer 1"). */
  const labelOf = (spec: ColumnSpec): string | null => {
    const store = services().store;
    const rec = store?.doc.lines.get(spec.line);
    const first = spec.lanes[0];
    if (rec === undefined || first === undefined) return null;
    const t = (k: string, p?: Readonly<Record<string, string | number>>): string => services().i18n.t(k, p);
    const name =
      spec.pair === 'position'
        ? t(m.propPosition)
        : spec.pair === 'size'
          ? t(m.propSize)
          : t(propNames(first.prop, spec).full);
    if (!spec.sumView && isLayered(first.prop) && first.layer > 0)
      return t(m.columnLabelLayer, { prop: name, layer: first.layer, line: rec.name });
    return t(m.columnLabel, { prop: name, line: rec.name });
  };

  /** Region, context menu, focusability and the accessible name of a column element (idempotent). */
  const decorate = (id: string) => (el: HTMLElement) => {
    watchMode(el);
    const spec = specOf(id);
    const label = spec === null ? null : labelOf(spec);
    if (label !== null && el.getAttribute('aria-label') !== label) el.setAttribute('aria-label', label);
  };

  /** The element of a laid-out column (a focusable motion region, see the instances), or null. */
  const elementOf = (id: string): HTMLElement | null => {
    const el = views.get(id)?.element() ?? columnElementIn(hostRoot(session.timeline()), id);
    if (el !== null && specOf(id) !== null) decorate(id)(el);
    return el;
  };
  session.columnElementOf = elementOf;

  const viewFor = (spec: ColumnSpec): MotionColumnView => {
    const existing = views.get(spec.id);
    if (existing !== undefined && !existing.disposed) return existing;
    const id = spec.id;
    const view = createMotionColumnView({
      id,
      scene: (axis, width) => {
        const sp = specOf(id);
        if (sp === null) return { lanes: [], pairLinked: null, compact: false, message: null };
        return columnScene(session, sp, axis, width, compact(id));
      },
      overlay: (axis, width) => {
        const sp = specOf(id);
        if (sp === null) return null;
        return columnOverlay(session, sp, axis, columnScene(session, sp, axis, width, compact(id)));
      },
      handlers: {
        ...handlers,
        pointerDown(v, e, hit) {
          if (compact(id)) {
            compactExpanded.add(id);
            session.relayout();
            return;
          }
          handlers.pointerDown?.(v, e, hit);
        },
      },
      decorateElement: decorate(id),
    });
    views.set(id, view);
    return view;
  };

  const widthOf = (spec: ColumnSpec): number =>
    compact(spec.id) ? COMPACT_WIDTH : spec.pair !== null ? PAIR_WIDTH : MOTION_WIDTH.default;

  /**
   * The instances of one of the two column types: full columns (`def`) or sparklines (`sparkDef`). Both compute the
   * same column set; a column moves between the types when it becomes compact or full.
   */
  const instancesOf = (host: TimelineHostApi, sparks: boolean): TimelineColumnInstance[] => {
    session.host = host;
    session.syncStore();
    const root = hostRoot(host);
    watchMode(root);
    sparkline = isSparklines(modeElementOf(root) ?? modeEl);
    const specs = compute();
    session.specs = specs;
    lastSignature = columnsSignature(specs);
    const ids = new Set(specs.map((s) => s.id));
    for (const id of [...instances.keys()]) {
      if (!ids.has(id)) {
        instances.delete(id);
        views.delete(id);
        compactExpanded.delete(id);
      }
    }
    dropSceneCache(session, ids);
    const defId = sparks ? MOTION_SPARK_DEF_ID : MOTION_DEF_ID;
    return specs
      .filter((spec) => compact(spec.id) === sparks)
      .map((spec) => {
        const view = viewFor(spec);
        const widthPx = widthOf(spec);
        const label = labelOf(spec) ?? undefined;
        const cur = instances.get(spec.id);
        if (cur?.view === view && cur.widthPx === widthPx && cur.label === label && cur.defId === defId) return cur;
        const inst: TimelineColumnInstance = {
          id: spec.id,
          defId,
          widthPx,
          header: headerComponent(),
          view,
          region: 'motion',
          contextMenu: 'motion.context',
          ...(label === undefined ? {} : { label }),
        };
        instances.set(spec.id, inst);
        return inst;
      });
  };

  const def: TimelineColumnDef = {
    id: MOTION_DEF_ID,
    title: { key: m.columnTitle },
    order: MOTION_ORDER,
    width: MOTION_WIDTH,
    instances: (host) => instancesOf(host, false),
  };

  const sparkDef: TimelineColumnDef = {
    id: MOTION_SPARK_DEF_ID,
    title: { key: m.columnTitle },
    order: MOTION_ORDER,
    width: MOTION_SPARK_WIDTH,
    instances: (host) => instancesOf(host, true),
  };

  const refresh = (): void => {
    if (disposed) return;
    const specs = compute();
    const sig = columnsSignature(specs);
    if (sig !== lastSignature) {
      lastSignature = sig;
      session.specs = specs;
      session.relayout();
    } else {
      // Same set; spec details (kind, level) may still refresh the scenes.
      session.specs = specs.map((s) => specOf(s.id) ?? s);
    }
    normalizeFocus(specs);
  };

  /**
   * A focused column of another layer than the base one whose property now shows as a Guided Σ column (the level
   * went to Guided) becomes the Σ column's base layer: K, the highlight and the hint bar then agree on it.
   */
  const normalizeFocus = (specs: readonly ColumnSpec[]): void => {
    const st = services().editor.getState();
    const f = st.focusedColumn;
    if (f === null || f.layer === 0 || !isLayered(f.prop) || findLane(specs, f) !== null) return;
    const base = { line: f.line, prop: f.prop, layer: 0 };
    if (findLane(specs, base)?.column.sumView === true) st.setFocusedColumn(base);
  };

  const watchStore = (): void => {
    for (const off of storeOffs) off();
    storeOffs = [];
    const store: DocStore | null = services().store;
    session.syncStore();
    if (store !== null) {
      storeOffs.push(
        store.subscribe((cs) => {
          if (
            cs.dirty.lineSet ||
            cs.dirty.lineMotion.size > 0 ||
            cs.dirty.lineProps.size > 0 ||
            cs.dirty.assets ||
            cs.dirty.pkg
          )
            refresh();
          // Live gesture frames only redraw; headers update once the gesture ends.
          if (cs.source !== 'gesture') session.touch();
          session.invalidate('content');
        }),
      );
    }
    refresh();
  };

  const onEditor = (st: EditorStore, prev: EditorStore): void => {
    // Columns shown in full next to an expanded editor go back to the normal rule when it opens or closes.
    if (st.timeline.expandedColumn !== prev.timeline.expandedColumn && !sparkline) compactExpanded.clear();
    if (
      st.activeLine !== prev.activeLine ||
      st.activeLayer !== prev.activeLayer ||
      st.pinnedColumns !== prev.pinnedColumns ||
      st.level !== prev.level ||
      st.timeline.showMotionColumns !== prev.timeline.showMotionColumns ||
      st.timeline.expandedColumn !== prev.timeline.expandedColumn
    ) {
      refresh();
    }
    if (st.selection !== prev.selection || st.focusedColumn !== prev.focusedColumn) {
      session.touch();
      session.invalidate('content');
    }
    if (st.selection !== prev.selection) revealSelection(st);
    if (st.placement !== prev.placement) session.invalidate('overlay');
    if (st.filters.mutedLayers !== prev.filters.mutedLayers) session.touch();
  };

  /**
   * Navigation hook (nav.reveal, Problems "Go", undo restoring a selection): selected keys of the active line that
   * sit only on another layer switch the edited layer (Full), so they are visible; a single selected key's column is
   * scrolled into view.
   */
  const revealSelection = (st: EditorStore): void => {
    const store = services().store;
    if (store === null || st.activeLine === null || st.selection.keys.size === 0) return;
    const layers = new Set<number>();
    let onActive = false;
    let only: { prop: ColumnSpec['lanes'][number]['prop']; layer: number } | null = null;
    for (const id of st.selection.keys) {
      const k = store.doc.keys.get(id);
      const owner = k === undefined ? undefined : store.doc.tracks.get(k.track)?.owner;
      if (owner?.kind !== 'line' || owner.line !== st.activeLine) continue;
      only = { prop: owner.prop, layer: owner.layer };
      if (owner.layer === st.activeLayer || !isLayered(owner.prop)) onActive = true;
      else layers.add(owner.layer);
    }
    if (levelOf(services()) === 'full' && !onActive && layers.size === 1) {
      const [layer] = [...layers];
      if (layer !== undefined) st.setActiveLayer(layer);
    }
    if (st.selection.keys.size === 1 && only !== null) {
      const host = session.timeline();
      const ctl = host === null ? null : asTimelineHostController(host);
      const found = findLane(session.specs, {
        line: st.activeLine,
        prop: only.prop,
        layer: isLayered(only.prop) ? only.layer : 0,
      });
      if (ctl !== null && found !== null) {
        try {
          ctl.revealColumn(found.column.id);
        } catch {
          // the host may not be laid out yet
        }
      }
    }
  };

  const controller: MotionController = {
    session,
    def,
    sparkDef,
    specOf,
    compact,
    toggleCompactExpanded(id) {
      if (compactExpanded.has(id)) compactExpanded.delete(id);
      else compactExpanded.add(id);
      session.relayout();
    },
    refresh,
    dispose() {
      if (disposed) return;
      disposed = true;
      for (const off of [...offs, ...storeOffs]) off();
      modeObserver?.disconnect();
      modeObserver = null;
      modeEl = null;
      if (session.columnElementOf === elementOf) session.columnElementOf = () => null;
      controllers.delete(session);
    },
  };

  offs.push(services().editor.subscribe(onEditor));
  offs.push(
    services().settings.subscribe(() => {
      refresh();
      session.touch();
    }),
  );
  offs.push(services().onChange(watchStore));
  watchStore();
  return controller;
}
